import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiServiceService } from '../../services/common/api-service.service';
import Swal from 'sweetalert2';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
@Component({
  selector: 'app-favorites',
  templateUrl: './favorites.component.html',
  styleUrls: ['./favorites.component.scss']
})
export class FavoritesComponent implements OnInit, OnDestroy {
  loading = false;
  private favoritesSubscription?: Subscription;
  activeSubTab: 'favorites' | 'filters' = 'favorites';
  
  favoriteProfiles: any[] = [];
  savedFilters: any[] = [];
  base64Images: { [key: string]: string } = {};

  profileNotes: Record<string, string> = {};
  tempNotes: Record<string, string> = {};
  editingNoteId: string | null = null;
  tempNoteText: string = '';

  constructor(
    private apiService: ApiServiceService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    if (!this.isLoggedIn) {
      this.router.navigate(['/login']);
      return;
    }
    this.favoritesSubscription = this.apiService.favoritesChanged.subscribe(() => {
      this.loadFavorites();
    });
    this.loadNotesFromStorage();
    this.loadFavorites();
    this.loadSavedFilters();
  }
  goBack(): void {
  this.location.back();
}
  loadNotesFromStorage(): void {
    try {
      const ownerId = localStorage.getItem('profileId') || 'default_user';
      const saved = localStorage.getItem(`matchmaker_notes_${ownerId}`);
      if (saved) {
        this.profileNotes = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading profile notes from storage', e);
    }
  }

  saveNotesToStorage(): void {
    try {
      const ownerId = localStorage.getItem('profileId') || 'default_user';
      localStorage.setItem(`matchmaker_notes_${ownerId}`, JSON.stringify(this.profileNotes));
    } catch (e) {
      console.error('Error saving profile notes to storage', e);
    }
  }

  get isLoggedIn(): boolean {
    return !!localStorage.getItem('sessionToken');
  }

  getMaskedName(fav: any): string {
    const profile = fav?.fullDetails || fav?.profile || fav;
    return `${profile?.basic_info?.first_name || ''} ${profile?.basic_info?.last_name || ''}`.trim() || 'Profile';
  }

  getAge(dateOfBirth: string): number {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  loadFavorites() {
    this.loading = true;
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) return;

    this.apiService.getFavorites(sessionToken, ownerId).subscribe(
      response => {
        if (response.status === 'success' && Array.isArray(response.data)) {
          const seen = new Set<string>();
          this.favoriteProfiles = response.data.map((fav: any) => {
            const profileId = fav.selected_profile_id?._id || fav.selected_profile_id || fav.profile?._id || fav.profile;
            return {
              ...fav,
              selected_profile_id: profileId ? String(profileId) : null
            };
          }).filter((fav: any) => {
            if (!fav.selected_profile_id) return false;
            if (seen.has(fav.selected_profile_id)) return false;
            seen.add(fav.selected_profile_id);
            return true;
          });
          this.favoriteProfiles.forEach(fav => {
            if (fav.selected_profile_id) {
              this.loadProfileDetails(fav.selected_profile_id, fav);
            }
          });
        }
        this.loading = false;
      },
      error => {
        console.error('Error loading favorites:', error);
        this.loading = false;
      }
    );
  }

  ngOnDestroy(): void {
    this.favoritesSubscription?.unsubscribe();
  }

  loadProfileDetails(profileId: string, favObj: any) {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileOwnerId = localStorage.getItem('profileId');
    if (!sessionToken || !profileOwnerId) return;
    
    this.apiService.getProfileDetails(sessionToken, profileId, profileOwnerId).subscribe(
      res => {
        if (res.status === 'success') {
          favObj.fullDetails = res.data;
          
          const idKey = res.data.basic_info?.keycloak_id || res.data.basic_info?.ory_identity_id || res.data._id || profileId;
          if (idKey && res.data.personal_info?.avatar_Id) {
            const imgUrl = `${idKey}/gallery/avatar/${res.data.personal_info.avatar_Id}`;
            this.loadImage(imgUrl, profileId);
          }
        }
      }
    );
  }

  loadImage(imagePath: string, profileId: string): void {
    if (this.base64Images[profileId]) return;
    this.apiService.getImageBase64(imagePath).subscribe(
      (data) => {
        if (data && data.base64Image) {
          this.base64Images[profileId] = data.base64Image;
        }
      },
      (error) => console.error('Error loading image base64:', error)
    );
  }

  removeFavorite(profileId: string) {
    this.loading = true;
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) return;

    this.apiService.deleteFavorite(sessionToken, profileId, ownerId).subscribe(
      response => {
        this.favoriteProfiles = this.favoriteProfiles.filter(f => f.selected_profile_id !== profileId);
        this.loading = false;
        this.apiService.favoritesChanged.next({ profileId, action: 'remove' });
        Swal.fire({
          title: 'Removed!',
          text: 'Profile removed from favorites.',
          icon: 'success',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000
        });
      },
      error => {
        console.error('Error deleting favorite:', error);
        this.loading = false;
        Swal.fire('Error', 'Could not remove favorite', 'error');
      }
    );
  }

  triggerInterest(profileId: string, favObj: any) {
    const sessionToken = localStorage.getItem('sessionToken');
    const ownerId = localStorage.getItem('profileId');
    if (!sessionToken || !ownerId) return;

    this.loading = true;
    this.apiService.setConnectionStatus(sessionToken, ownerId, profileId, 'Pending').subscribe(
      response => {
        this.loading = false;
        favObj.interestSent = true;
        Swal.fire({
          icon: 'success',
          title: 'Interest Dispatched',
          text: `Mutual interest handshake has been delivered.`,
          confirmButtonColor: '#8c002a'
        });
      },
      error => {
        this.loading = false;
        if (this.apiService.handleQuotaError(error)) return;
        Swal.fire({
          title: 'Error',
          text: 'Could not send interest. Please try again.',
          icon: 'error',
          confirmButtonColor: '#8c002a'
        });
      }
    );
  }

  viewProfile(profileId: string) {
    this.router.navigate(['/profile-details', profileId]);
  }

  loadSavedFilters() {
    const sessionToken = localStorage.getItem('sessionToken');
    const profileId = localStorage.getItem('profileId');
    if (!sessionToken || !profileId) return;

    this.apiService.getSaveFilters(sessionToken, profileId).subscribe(
      response => {
        if (response.status === 'success' && Array.isArray(response.data)) {
          this.savedFilters = response.data;
        }
      },
      error => console.error('Error fetching saved filters:', error)
    );
  }

  handleDeleteFilterConfirm(filter: any) {
    const sessionToken = localStorage.getItem('sessionToken');
    if (!sessionToken) return;

    Swal.fire({
      title: 'Delete Preset Filter?',
      text: `Are you sure you want to remove the saved filter?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete',
      confirmButtonColor: '#8c002a',
      cancelButtonColor: '#635d5e'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        this.apiService.deleteFilter(sessionToken, filter._id).subscribe(
          response => {
            this.savedFilters = this.savedFilters.filter(f => f._id !== filter._id);
            this.loading = false;
            Swal.fire({
              toast: true,
              position: 'top-end',
              icon: 'success',
              title: 'Filter deleted',
              showConfirmButton: false,
              timer: 1500
            });
          },
          error => {
            console.error('Error deleting filter:', error);
            this.loading = false;
            Swal.fire('Error', 'Could not delete filter', 'error');
          }
        );
      }
    });
  }

  runSavedFilterQuery(filterId: string, filterName: string) {
    Swal.fire({
      title: `Running Search Preset`,
      text: `Searching for matches matching "${filterName}"...`,
      icon: 'info',
      timer: 1000,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    }).then(() => {
      this.router.navigate(['/profiles'], { queryParams: { filterId: filterId } });
    });
  }

  startEditingNote(id: string) {
    this.editingNoteId = id;
    this.tempNotes[id] = this.profileNotes[id] || '';
    this.tempNoteText = this.tempNotes[id];
  }

  saveNote(id: string) {
    const noteContent = this.tempNotes[id] !== undefined ? this.tempNotes[id] : this.tempNoteText;
    this.profileNotes[id] = (noteContent || '').trim();
    this.editingNoteId = null;
    this.saveNotesToStorage();
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Shortlist note saved',
      showConfirmButton: false,
      timer: 1500
    });
  }

  getLocationLine(fav: any): string {
    const profile = fav?.fullDetails || fav?.profile || fav;
    const loc = profile?.residing_location || {};
    const city = loc?.city_id?.city_nme || (typeof loc?.city_id === 'string' ? loc.city_id : null);
    const state = loc?.state_id?.name_1 || loc?.state_id?.state_nme || (typeof loc?.state_id === 'string' ? loc.state_id : null);
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return 'Not specified';
  }

  getProfession(fav: any): string {
    const profile = fav?.fullDetails || fav?.profile || fav;
    const occ = profile?.professional_info?.occupation_id;
    if (typeof occ === 'object' && occ) return occ.occupation_nme || 'Not specified';
    return 'Not specified';
  }
}
